import { GrundartikelLink } from '@/components/catalog/GrundartikelLink'
import { useEffect, useRef, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { toast } from 'sonner'
import {
  useReceiptDetail, useReceiptAction, useWeReportPalettes, useAcceptCaptured, useCorrectPositionQty, useConfirmWePackstation, useSetWePackstation, useSetWeAbmessung, useWePackstationVorschlag, downloadPdf,
  type ReceiptLabel, type ReceiptDetail,
} from '@/api/warehouse'
import { newIdempotencyKey } from '@/lib/offline-queue'
import { ApiError } from '@/api/client'
import { weUploadHinweis, weEtikettZeile } from '@/lib/we-anzeige'
import { VorabBlock } from './components/VorabBlock'
import { useAuth } from '@/api/auth'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { Label } from '@/components/ui/label'
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog'

const statusNames: Record<string, [string, string]> = {
  offen: ['Offen', 'bg-blue-100 text-blue-900'], teilweise: ['Teilweise', 'bg-amber-100 text-amber-900'],
  abweichung: ['Abweichung', 'bg-red-100 text-red-900'], vollstaendig: ['Vollständig', 'bg-green-100 text-green-900'],
  abgeschlossen: ['Abgeschlossen', 'bg-gray-100 text-gray-900'], storniert: ['Storniert', 'bg-gray-100 text-gray-900'],
  eskaliert: ['Eskaliert', 'bg-red-100 text-red-900'],
}
function StatusPill({ status }: { status: string }) {
  const [label, cls] = statusNames[status] ?? [status, 'bg-gray-100 text-gray-900']
  return <span className={`inline-block rounded px-2 py-1 text-xs font-medium ${cls}`}>{label}</span>
}

const REASONS = [
  ['weniger_geliefert', 'Weniger Ware angekommen'],
  ['beschaedigt', 'Ware beschädigt'],
  ['falscher_artikel', 'Falscher Artikel geliefert'],
  ['sonstiges', 'Sonstiges'],
] as const

function PaletteCorrectDialog({
  open,
  onOpenChange,
  label,
  booked,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  label: ReceiptLabel | null
  booked: ReceiptDetail['bookedInfo'][number] | undefined
}) {
  const [cartons, setCartons] = useState<number | ''>('')
  const [piecesPerCarton, setPiecesPerCarton] = useState<number | ''>('')
  const [restPieces, setRestPieces] = useState<number | ''>('')
  const [reason, setReason] = useState('')
  // Plan 2026-10-20 Teil A: Rueckfrage „Paare statt Stueck gezaehlt?“ (409 mit field paare_bestaetigt).
  // Der Key bleibt fuer genau diesen Korrekturversuch stehen; „Ja, Stück korrekt“ sendet ihn mit Flag erneut.
  const [paareFrage, setPaareFrage] = useState<string | null>(null)
  const keyRef = useRef(newIdempotencyKey())

  const correct = useCorrectPositionQty()

  useEffect(() => {
    if (open && booked) {
      setCartons(booked.cartons ?? 1)
      setPiecesPerCarton(booked.piecesPerCarton ?? 1)
      setRestPieces(booked.restPieces ?? 0)
      setReason('')
      setPaareFrage(null)
      keyRef.current = newIdempotencyKey()
    }
  }, [open, booked])

  const numCartons = typeof cartons === 'number' ? cartons : 0
  const numPieces = typeof piecesPerCarton === 'number' ? piecesPerCarton : 0
  const numRest = typeof restPieces === 'number' ? restPieces : 0
  const total = (numCartons > 0 && numPieces > 0) ? (numCartons * numPieces + numRest) : 0

  const isValid = numCartons >= 1 && numPieces >= 1 && numRest >= 0 && reason.trim().length >= 3

  async function senden(paareBestaetigt: boolean) {
    if (!isValid || !booked?.positionId || correct.isPending) return
    try {
      await correct.mutateAsync({
        positionId: booked.positionId,
        cartons: numCartons,
        pieces_per_carton: numPieces,
        rest_pieces: numRest,
        reason: reason.trim(),
        idempotency_key: keyRef.current,
        ...(paareBestaetigt ? { paare_bestaetigt: true } : {}),
      })
      toast.success('Palette erfolgreich korrigiert')
      setPaareFrage(null)
      onOpenChange(false)
    } catch (err) {
      if (err instanceof ApiError && err.field === 'paare_bestaetigt') { setPaareFrage(err.message); return }
      toast.error(err instanceof Error ? err.message : 'Korrektur fehlgeschlagen')
    }
  }
  function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    void senden(false)
  }
  // „Neu zählen“: Rueckfrage schliessen, Eingabe bleibt zum Aendern offen; neuer Versuch = neuer Key.
  function neuZaehlen() {
    setPaareFrage(null)
    keyRef.current = newIdempotencyKey()
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Palette korrigieren</DialogTitle>
          <DialogDescription>
            {label ? `Palette ${label.paletteNo ?? `#${label.id}`} (Zettel ${label.sheetNo})` : 'Palette korrigieren'}
            {booked?.placeBarcode ? ` · Platz ${booked.placeBarcode}` : ''}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4 py-2">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="correct-cartons">Kartons (Anzahl)</Label>
              <Input
                id="correct-cartons"
                type="number"
                min="1"
                step="1"
                required
                value={cartons}
                onChange={(e) => setCartons(e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value) || 0))}
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="correct-pieces">Stück pro Karton</Label>
              <Input
                id="correct-pieces"
                type="number"
                min="1"
                step="1"
                required
                value={piecesPerCarton}
                onChange={(e) => setPiecesPerCarton(e.target.value === '' ? '' : Math.max(1, parseInt(e.target.value) || 0))}
              />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 items-end">
            <div className="space-y-1.5">
              <Label htmlFor="correct-rest">Reststücke (lose)</Label>
              <Input
                id="correct-rest"
                type="number"
                min="0"
                step="1"
                value={restPieces}
                onChange={(e) => setRestPieces(e.target.value === '' ? '' : Math.max(0, parseInt(e.target.value) || 0))}
              />
            </div>
            <div className="rounded-md border border-line bg-muted/20 p-2 text-center">
              <div className="text-xs text-muted">Bisher {booked?.cartons != null && booked.piecesPerCarton != null ? booked.cartons * booked.piecesPerCarton + (booked.restPieces ?? 0) : label?.qty ?? "–"} → neu</div>
              <div className="text-lg font-bold text-primary">{total} St.</div>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="correct-reason">Begründung (Pflichtangabe)</Label>
            <Input
              id="correct-reason"
              placeholder="z. B. 30 Stk/Karton statt 20 laut Lieferschein"
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {reason.trim().length > 0 && reason.trim().length < 3 && (
              <p className="text-xs text-destructive">Mindestens 3 Zeichen erforderlich</p>
            )}
          </div>

          {paareFrage && (
            <div role="alert" className="rounded-md border-2 border-amber-500 bg-amber-50 p-3 space-y-2 text-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
              <div className="font-semibold">{paareFrage}</div>
              <div className="flex flex-wrap gap-2">
                <Button type="button" disabled={correct.isPending} onClick={() => void senden(true)}>Ja, Stück korrekt</Button>
                <Button type="button" variant="outline" disabled={correct.isPending} onClick={neuZaehlen}>Neu zählen</Button>
              </div>
            </div>
          )}

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            {!paareFrage && <><Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button type="submit" disabled={!isValid || correct.isPending || paareFrage !== null}>
              {correct.isPending ? 'Speichere…' : 'Korrektur buchen'}
            </Button></>}
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function ItemCancelDialog({
  open,
  onOpenChange,
  item,
  receiptId,
  openLabels,
}: {
  open: boolean
  onOpenChange: (open: boolean) => void
  openLabels: number
  item: ReceiptDetail['items'][number] | null
  receiptId: number
}) {
  const [reason, setReason] = useState('')
  const actions = useReceiptAction(receiptId)

  useEffect(() => {
    if (open) setReason('')
  }, [open])

  const isValid = reason.trim().length >= 3

  async function onSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isValid || !item) return
    try {
      await actions.cancelItem.mutateAsync({
        itemId: item.id,
        reason: reason.trim(),
      })
      toast.success(`Position ${item.merchantSku} erfolgreich storniert`)
      onOpenChange(false)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Positionsstorno fehlgeschlagen')
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Position stornieren</DialogTitle>
          <DialogDescription>
            {item ? (
              <>
                Es werden {openLabels} offene Etiketten storniert. Soll die Position <b><GrundartikelLink code={item.merchantSku} /></b> ({item.itemName ?? 'Ohne Bezeichnung'}) mit Sollmenge <b>{item.totalQty} St.</b> wirklich storniert werden?
              </>
            ) : (
              'Position stornieren'
            )}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={(e) => void onSubmit(e)} className="space-y-4 py-2">
          <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
            ⚠ Alle noch offenen Etiketten dieser Position werden storniert. Der WE-Auftrag wird anschließend automatisch neu berechnet. Bereits erfasste Paletten können nicht storniert werden.
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="cancel-item-reason">Stornogrund (Pflichtangabe)</Label>
            <Input
              id="cancel-item-reason"
              placeholder="z. B. Nicht geliefert / Storno durch Lieferant"
              required
              value={reason}
              onChange={(e) => setReason(e.target.value)}
            />
            {reason.trim().length > 0 && reason.trim().length < 3 && (
              <p className="text-xs text-destructive">Mindestens 3 Zeichen erforderlich</p>
            )}
          </div>

          <DialogFooter className="gap-2 sm:gap-0 pt-2">
            <Button type="button" variant="ghost" onClick={() => onOpenChange(false)}>
              Abbrechen
            </Button>
            <Button
              type="submit"
              variant="destructive"
              disabled={!isValid || actions.cancelItem.isPending}
            >
              {actions.cancelItem.isPending ? 'Storniere…' : 'Position stornieren'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

function LabelRow({ l, booked, canLead, busy, isV2, onCancel, onDuplicate, onCorrectPalette }: {
  l: ReceiptLabel
  booked: ReceiptDetail['bookedInfo'][number] | undefined
  canLead: boolean
  busy: boolean
  isV2?: boolean
  onCancel: (id: number) => void
  onDuplicate: (id: number) => void
  onCorrectPalette?: (l: ReceiptLabel, booked: ReceiptDetail['bookedInfo'][number]) => void
}) {
  const color = l.status === 'booked' ? 'text-green-700' : l.status === 'cancelled' ? 'text-muted line-through' : ''
  // WP4: Menge und Zustand kommen aus weEtikettZeile() (src/lib/we-anzeige.ts) — dort
  // sind die Grenzfaelle einzeln pruefbar. Frueher stand der gebuchte Zweig unter
  // `booked &&`: fehlte der bookedInfo-Eintrag, blieb die Zelle LEER und eine gebuchte
  // Palette sah aus wie gar nichts. Jetzt zeigt sie „gebucht" und markiert die fehlenden
  // Felder mit „?“.
  const zeile = weEtikettZeile(l, booked)
  return (
    <tr className="border-t border-line">
      <td className={`p-2 font-mono ${color}`}>Zettel {l.sheetNo}</td>
      <td className={`p-2 font-mono ${color}`}>{l.paletteNo}</td>
      {/* Task 11: V2-Etiketten tragen qty=NULL bis zur Erfassung am Regal — dann
          erscheint statt einer Mengenangabe der Platzhalter. */}
      <td className={`p-2 ${color}`}>{zeile.menge}</td>
      <td className={`p-2 ${color}`}>
        <span>{l.status === 'booked' ? 'Gebucht' : l.status === 'valid' ? 'Offen' : 'Storniert'}{booked?.placeBarcode ? ` · ${booked.placeBarcode}` : ''}</span>
        {(booked || l.duplicateCount > 0) && <Popover><PopoverTrigger asChild><Button size="sm" variant="ghost" aria-label={`Informationen zu Zettel ${l.sheetNo}`}>Info</Button></PopoverTrigger><PopoverContent><p>{zeile.zustand}</p><p>{booked?.bookedBy ?? '–'} · {booked?.bookedAt ? new Date(booked.bookedAt).toLocaleString('de-DE') : '?'}</p>{l.duplicateCount > 0 && <p>Duplikate: {l.duplicateCount}</p>}</PopoverContent></Popover>}
        {l.duplicateCount > 0 && <span className="ml-2 text-xs bg-red-100 text-red-700 px-1 rounded">DUPLIKAT ×{l.duplicateCount}</span>}
      </td>
      <td className="p-2 space-x-1">
        {canLead && l.status === 'valid' && <Button size="sm" variant="outline" disabled={busy} onClick={() => onCancel(l.id)}>Stornieren</Button>}
        {canLead && l.status !== 'cancelled' && <Button size="sm" variant="ghost" disabled={busy} onClick={() => onDuplicate(l.id)}>Duplikat</Button>}
        {isV2 && canLead && l.status === 'booked' && booked?.positionId != null && (
          <Button size="sm" variant="outline" disabled={busy} onClick={() => booked && onCorrectPalette?.(l, booked)}>Palette korrigieren</Button>
        )}
      </td>
    </tr>
  )
}

// Task 11: je Position (d.items.map) ein eigener Abschnitt mit eigener
// Etiketten-Tabelle + Lead-Aktion "Weitere Etiketten (Ziel-Palettenzahl)".
// useWeReportPalettes wird HIER (nicht im .map()-Callback der Elternseite)
// aufgerufen — ein Hook-Aufruf pro Positions-Komponenteninstanz ist
// Rules-of-Hooks-konform, ein Hook-Aufruf innerhalb eines .map()-Callbacks waere es nicht.
function PositionSection({
  item, receiptId, labels, bookedInfo, canLead, closed, rowBusy, onCancel, onDuplicate, onCorrectPalette, onCancelItem,
}: {
  item: ReceiptDetail['items'][number]
  receiptId: number
  labels: ReceiptLabel[]
  bookedInfo: ReceiptDetail['bookedInfo']
  canLead: boolean
  closed: boolean
  rowBusy: boolean
  onCancel: (id: number) => void
  onDuplicate: (id: number) => void
  onCorrectPalette: (l: ReceiptLabel, booked: ReceiptDetail['bookedInfo'][number]) => void
  onCancelItem: (item: ReceiptDetail['items'][number]) => void
}) {
  const report = useWeReportPalettes(item.id)
  const accept = useAcceptCaptured(receiptId)
  const confirm = useConfirmWePackstation(receiptId, item.id)
  const [ist, setIst] = useState<string | null>(null)
  const [confirmError, setConfirmError] = useState<string | null>(null)
  const confirmBusyRef = useRef(false)
  const istValue = ist ?? String(item.divertedCartons ?? 0)
  const istCartons = Number(istValue)
  const validIst = istValue !== '' && Number.isInteger(istCartons) && istCartons >= 0 && istCartons <= item.divertedCartons
  const directOnly = item.divertedCartons > 0 && item.totalQty === 0 && item.cartonsDelivered === 0
  const confirmed = item.packstationConfirmedAt != null || confirm.isSuccess
  // Plan 2026-10-16: Leiter aendert die Packstation-Kartons, solange keine Palette erfasst ist.
  const setD = useSetWePackstation(receiptId, item.id)
  const [dEingabe, setDEingabe] = useState<string | null>(null)
  const kartonsGesamt = item.cartonsDelivered == null ? null : item.cartonsDelivered + item.divertedCartons
  const paletteErfasst = item.capturedQty > 0 || labels.some(l => l.status === 'booked')
  const kannDAendern = canLead && !closed && item.status !== 'storniert' && !confirmed && !paletteErfasst && kartonsGesamt != null
  const dValue = dEingabe ?? String(item.divertedCartons)
  const dZahl = Number(dValue)
  const dGueltig = dValue !== '' && Number.isInteger(dZahl) && kartonsGesamt != null && dZahl >= 0 && dZahl <= kartonsGesamt
  async function onSetD() {
    if (!kannDAendern || !dGueltig || setD.isPending) return
    try {
      const res = await setD.mutateAsync({ cartons: dZahl })
      setDEingabe(null)
      toast.success(!res.changed ? 'Packstation-Kartons unverändert'
        : `Packstation-Kartons gespeichert: ${res.diverted_cartons}`
          + (res.neues_etikett ? ` · neues Etikett ${res.neues_etikett} drucken` : ''))
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Packstation-Kartons nicht gespeichert')
    }
  }
  // Plan 2026-10-17: Mass (Breite x Laenge) steht fett unter der Sternnummer und auf den
  // Seiten-Etiketten. Server normalisiert gleich (Whitespace) und begrenzt auf 30 Zeichen.
  const setMass = useSetWeAbmessung(receiptId, item.id)
  const [massEingabe, setMassEingabe] = useState<string | null>(null)
  const massGespeichert = item.abmessung ?? ''
  const massValue = massEingabe ?? massGespeichert
  const massNeu = massValue.replace(/\s+/g, ' ').trim()
  const massZuLang = [...massNeu].length > 30
  const istV2 = item.cartonsDelivered != null
  const kannMassAendern = canLead && !closed && item.status !== 'storniert' && istV2
  async function onSetMass() {
    if (!kannMassAendern || massZuLang || massNeu === massGespeichert || setMass.isPending) return
    try {
      const res = await setMass.mutateAsync({ abmessung: massNeu === '' ? null : massNeu })
      setMassEingabe(null)
      toast.success(!res.changed ? 'Maß unverändert' : res.abmessung ? `Maß gespeichert: ${res.abmessung}` : 'Maß entfernt')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Maß nicht gespeichert')
    }
  }
  async function onConfirmPackstation() {
    if (!canLead || closed || item.status === 'storniert' || confirmed || !validIst || confirmBusyRef.current) return
    confirmBusyRef.current = true
    setConfirmError(null)
    try { await confirm.mutateAsync({ cartons: istCartons }) }
    catch (e) { setConfirmError(e instanceof Error ? e.message : 'Einräumen konnte nicht bestätigt werden') }
    finally { confirmBusyRef.current = false }
  }

  // OI-130 P2: Leiter uebernimmt die erfasste Menge als neues Soll — mit Rueckfrage,
  // weil damit der Klaerfall dieser Position aufgeloest und der Auftrag vollstaendig wird.
  const [acceptOpen, setAcceptOpen] = useState(false)
  const [acceptQuestion, setAcceptQuestion] = useState<string | null>(null)
  const acceptKey = useRef(newIdempotencyKey())
  function openAccept() { acceptKey.current = newIdempotencyKey(); setAcceptQuestion(null); setAcceptOpen(true) }
  async function onAcceptCaptured(paareBestaetigt: boolean) {
    if (accept.isPending) return
    try {
      const res = await accept.mutateAsync({ receiptItemId: item.id, idempotency_key: acceptKey.current, paare_bestaetigt: paareBestaetigt })
      toast.success(res.replay ? 'Bereits übernommen — Position ist vollständig' : `Ist übernommen — Soll jetzt ${res.qty} St.${res.cartons != null ? ` / ${res.cartons} Kartons` : ''}`)
      setAcceptOpen(false)
    } catch (e) {
      if (!paareBestaetigt && e instanceof ApiError && e.field === 'paare_bestaetigt') { setAcceptQuestion(e.message); return }
      toast.error(e instanceof Error ? e.message : 'Übernahme fehlgeschlagen')
    }
  }
  // Gleiches Key-Ref-Muster wie der bisherige regenKeyRef (RA-BI): EIN stabiler
  // Idempotency-Key pro Meldeversuch, bleibt bei Fehlschlag/Retry unveraendert,
  // wird erst nach Erfolg neu gezogen.
  const keyRef = useRef(newIdempotencyKey())
  const [targetCount, setTargetCount] = useState(0)
  // Haelt ueber Mutation UND die beiden nachfolgenden PDF-Downloads an — ein
  // Re-Klick waehrend des Downloads darf keine zweite Meldung ausloesen
  // (gleiches Muster wie duplicateBusy in der Elternseite).
  const [pdfBusy, setPdfBusy] = useState(false)

  async function onReportPalettes() {
    if (report.isPending || pdfBusy || targetCount < 1) return
    setPdfBusy(true)
    try {
      const res = await report.mutateAsync({ target_count: targetCount, idempotency_key: keyRef.current })
      keyRef.current = newIdempotencyKey()
      toast.success(res.duplicate ? 'Meldung bereits verarbeitet (Duplikat erkannt)' : `${res.created} neue Etikette(n) erzeugt — PDFs laden`)
      // WP4: Die Ablage-Meldung darf nur bei BESTAETIGTEM Graph-Upload wie eine Ablage
      // klingen — Begruendung und Grenzfaelle in src/lib/we-anzeige.ts. Der Warn-Zweig
      // ist neu: bisher blieb ein ausgefallener Upload hier voellig stumm.
      const hinweis = weUploadHinweis(res.upload_sent, res.onedrive_path)
      if (hinweis.art === 'ok') toast.info(hinweis.text)
      else toast.warning(hinweis.text)
      const label_ids = res.labels.map((l) => l.label_id)
      await downloadPdf(`/warehouse/receipt/${receiptId}/we-front-pdf/`, `we-front-${item.merchantSku}.pdf`, { label_ids })
      await downloadPdf(`/warehouse/receipt/${receiptId}/we-side-pdf/`, `we-seiten-${item.merchantSku}.pdf`, { label_ids })
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Fehlgeschlagen')
    } finally {
      setPdfBusy(false)
    }
  }

  // QR-Etiketten für EINE Palette (User 2026-07-17): die zwei kleinen QR-Zettel entstehen
  // sonst erst mit der Palettenzahl-Meldung des Wareneingangs. Hier druckbar, sobald der
  // Auftrag steht — bewusst nur für die erste (Anlage-)Palette, nichts wird neu erzeugt.
  const ersteOffenePalette = labels.find((l) => l.status !== 'cancelled')
  async function onQrEinePalette() {
    if (!ersteOffenePalette || pdfBusy) return
    setPdfBusy(true)
    try {
      await downloadPdf(
        `/warehouse/receipt/${receiptId}/we-side-pdf/`,
        `we-qr-${item.merchantSku}-${ersteOffenePalette.paletteNo ?? ersteOffenePalette.id}.pdf`,
        { label_ids: [ersteOffenePalette.id] },
      )
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'QR-Etiketten konnten nicht geladen werden')
    } finally {
      setPdfBusy(false)
    }
  }

  const isStorniert = item.status === 'storniert'

  return (
    <div className={`rounded-xl border border-line bg-panel p-4 space-y-3 `}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="font-semibold flex items-center gap-2">
          <span className={isStorniert ? 'line-through text-muted' : ''}>
            <GrundartikelLink code={item.merchantSku} /> — {item.itemName ?? '?'}
          </span>
          {isStorniert ? (
            <StatusPill status={item.status} />
          ) : (
            <StatusPill status={item.status} />
          )}
        </div>
        {canLead && !isStorniert && item.capturedQty === 0 && (
          <Button
            size="sm"
            variant="outline"
            className="text-red-600 border-red-200 hover:bg-red-50 hover:text-red-700"
            disabled={rowBusy}
            onClick={() => onCancelItem(item)}
          >
            Position stornieren
          </Button>
        )}
      </div>

      {isStorniert && (
        <div className="text-sm rounded-md border border-line bg-muted/20 p-2 text-muted-foreground">
          Storniert{item.cancelledAt ? ` am ${new Date(item.cancelledAt).toLocaleString('de-DE')}` : ''}
          {item.cancelledByName ? ` von ${item.cancelledByName}` : ''}
          {item.cancelReason ? ` — Grund: ${item.cancelReason}` : ''}
        </div>
      )}

      {/* OI-130 P2: Soll und Ist nebeneinander — der Grund fuer 'teilweise' trotz
          N/N erfasster Paletten war vorher nirgends ablesbar. */}
      <div className="text-sm flex flex-wrap gap-x-6 gap-y-1">
        <span>Paletten: <b>{labels.filter(l => l.status === 'booked').length} / {labels.filter(l => l.status !== 'cancelled').length}</b></span>
        <span>Soll: <b>{item.totalQty} St.</b>{item.cartonsDelivered != null && <> · {item.cartonsDelivered} Kartons</>}</span>
        <span>Ist (erfasst): <b>{item.capturedQty} St.</b> · {item.capturedCartons} Kartons</span>
        {!kannMassAendern && istV2 && item.abmessung && <span>Maß auf den Etiketten: <b>{item.abmessung}</b></span>}
        {item.zaehlweise && (
          <span>Zählweise: <b>{item.zaehlweise.anzeige}</b>{item.zaehlweise.hinweis ? <> · {item.zaehlweise.hinweis}</> : null}</span>
        )}
      </div>
      {(kannMassAendern || (!directOnly && !isStorniert && canLead && item.cartonsDelivered != null)) && <details><summary className="cursor-pointer text-sm font-medium">Bearbeiten</summary>
        <div className="pt-2">
      {kannMassAendern && (
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <label htmlFor={`abmessung-${item.id}`}>Maß auf den Etiketten (Breite × Länge)
            <Input id={`abmessung-${item.id}`} aria-label={`Maß für ${item.merchantSku}`} type="text"
              placeholder="z. B. 0,8 m × 30 m" value={massValue} aria-invalid={massZuLang}
              className="mt-1 w-56" disabled={setMass.isPending || rowBusy} onChange={e => setMassEingabe(e.target.value)} />
          </label>
          <Button variant="outline" disabled={setMass.isPending || rowBusy || massZuLang || massNeu === massGespeichert}
            onClick={() => void onSetMass()}>
            {setMass.isPending ? 'Speichere…' : 'Maß speichern'}
          </Button>
          {massZuLang
            ? <span role="alert" className="text-xs text-red-600 dark:text-red-400">Höchstens 30 Zeichen.</span>
            : <span className="text-xs text-muted">Steht fett unter der Sternnummer. Leer lassen, wenn der Artikel kein Maß hat.</span>}
        </div>
      )}
          {!directOnly && !isStorniert && canLead && item.cartonsDelivered != null && <div className="flex flex-wrap gap-2 items-center pt-2"><span>Etiketten für Paletten bestellen</span><Input className="w-24" type="number" placeholder="Ziel" value={targetCount || ''} onChange={e => setTargetCount(+e.target.value)} /><Button variant="outline" disabled={report.isPending || pdfBusy || targetCount < 1} onClick={() => void onReportPalettes()}>Etiketten für Paletten bestellen</Button></div>}
        </div></details>}
      <VorabBlock receiptId={receiptId} receiptItemId={item.id} sourceQty={item.packstationSourceQty ?? 0}
        divertedCartons={item.divertedCartons} enabled={canLead && istV2 && item.status !== 'storniert'}
        neuErlaubt={!closed && !confirmed && !paletteErfasst} rowBusy={rowBusy} />
      {kannDAendern && (
        <div className="flex flex-wrap items-end gap-2 text-sm">
          <label htmlFor={`packstation-d-${item.id}`}>Kartons direkt in die Packstation (0–{kartonsGesamt})
            <Input id={`packstation-d-${item.id}`} aria-label={`Packstation-Kartons für ${item.merchantSku}`}
              type="number" min={0} max={kartonsGesamt ?? undefined} step={1} value={dValue}
              className="mt-1 w-24" disabled={setD.isPending || rowBusy} onChange={e => setDEingabe(e.target.value)} />
          </label>
          <Button variant="outline" disabled={setD.isPending || rowBusy || !dGueltig || dZahl === item.divertedCartons}
            onClick={() => void onSetD()}>
            {setD.isPending ? 'Speichere…' : 'Packstation-Kartons speichern'}
          </Button>
          <span className="text-xs text-muted">Änderbar bis zur ersten erfassten Palette.</span>
        </div>
      )}
      {item.divertedCartons > 0 && (
        <div className="rounded-lg border border-line bg-panel-2 p-3 space-y-2 text-sm">
          <div>Direkt zur Packstation: {item.divertedCartons} Kartons · {item.packstationSourceQty == null || item.packstationPiecesPerCarton == null
            ? 'unbekannt' : Math.min(item.divertedCartons * item.packstationPiecesPerCarton, item.packstationSourceQty)} St.</div>
          {confirmed ? <div className="font-medium">
            Eingeräumt: {item.packstationConfirmedCartons ?? confirm.data?.cartons} Kartons · {item.packstationConfirmedStueck ?? confirm.data?.stueck} St.
            {item.packstationConfirmedByName ? ` · ${item.packstationConfirmedByName}` : ''}
            {item.packstationConfirmedAt ? ` · ${new Date(item.packstationConfirmedAt).toLocaleString('de-DE', { timeZone: 'Europe/Berlin' })}` : ' · Bestätigt; lade Wer/Wann…'}
          </div> : canLead && !isStorniert && !closed ? <div className="flex flex-wrap items-end gap-2">
            <label htmlFor={`packstation-ist-${item.id}`}>Ist-Kartons (0–{item.divertedCartons})
              <Input id={`packstation-ist-${item.id}`} aria-label={`Ist-Kartons Packstation für ${item.merchantSku}`}
                type="number" min={0} max={item.divertedCartons} step={1} value={istValue}
                className="mt-1 w-24" disabled={confirm.isPending || rowBusy} onChange={e => setIst(e.target.value)} />
            </label>
            <Button variant="outline" disabled={confirm.isPending || rowBusy || !validIst} onClick={() => void onConfirmPackstation()}>
              {confirm.isPending ? 'Bestätige…' : 'Packstation-Kartons eingeräumt'}
            </Button>
          </div> : <div className="text-muted">Einräumen noch nicht bestätigt.</div>}
          {confirmError && <div role="alert" className="text-red-600 dark:text-red-400">{confirmError}</div>}
          {!confirmed && <div className="text-xs text-muted">Erst nach dem Einräumen bestätigen. Spätere Mengenkorrekturen erfolgen durch Neuzählung.</div>}
        </div>
      )}
      {!isStorniert && item.deviationMessage && (
        <div className="text-sm rounded-md border border-red-600/40 bg-red-600/5 text-red-700 p-2 flex flex-wrap items-center gap-2">
          <span className="flex-1 min-w-[220px]">⚠ {item.deviationMessage}</span>
          {canLead && (
            <Button size="sm" variant="outline" disabled={accept.isPending} onClick={openAccept}>
              {accept.isPending ? 'Übernehme…' : 'Ist als Soll übernehmen'}
            </Button>
          )}
        </div>
      )}
      <Dialog open={acceptOpen} onOpenChange={setAcceptOpen}><DialogContent><DialogHeader><DialogTitle>Ist als Soll übernehmen</DialogTitle><DialogDescription>{item.merchantSku} · Der Mengenklärfall wird aufgelöst.</DialogDescription></DialogHeader><p>Soll {item.totalQty} → Ist {item.capturedQty} St. · {item.cartonsDelivered ?? '–'} → {item.capturedCartons} Kartons</p>{acceptQuestion && <p role="alert">{acceptQuestion}</p>}<DialogFooter><Button variant="outline" onClick={() => setAcceptOpen(false)}>Abbrechen</Button>{acceptQuestion ? <Button disabled={accept.isPending} onClick={() => void onAcceptCaptured(true)}>Ja, Stück korrekt</Button> : <Button disabled={accept.isPending} onClick={() => void onAcceptCaptured(false)}>Ist übernehmen</Button>}</DialogFooter></DialogContent></Dialog>
      {!directOnly && <div className="overflow-x-auto rounded-lg border border-line">
        <table className="w-full text-sm">
          <thead className="bg-panel text-left"><tr><th className="p-2">Zettel</th><th className="p-2">Paletten-Nr</th><th className="p-2">Menge</th><th className="p-2">Status · Platz</th><th className="p-2"></th></tr></thead>
          <tbody>
            {labels.map((l) => (
              <LabelRow
                key={l.id}
                l={l}
                booked={bookedInfo[l.id]}
                canLead={canLead}
                busy={rowBusy}
                isV2={item.cartonsDelivered !== null}
                onCancel={onCancel}
                onDuplicate={onDuplicate}
                onCorrectPalette={onCorrectPalette}
              />
            ))}
          </tbody>
        </table>
      </div>}
      {/* F5-Fix (2026-07-17): Der Ziel-Palettenzahl-Block ruft den V2-Endpoint
          /we/items/:id/paletten/, der V1-Positionen (cartonsDelivered NULL) mit 409
          abweist — fuer V1-Positionen deshalb gar nicht anzeigen (409-Sackgasse).
          V1-Nachdruck laeuft ueber den Legacy-Regenerate-Block unten. */}
      {!directOnly && !isStorniert && ersteOffenePalette && (
        <div className="flex flex-wrap gap-2 items-center">
          <Button variant="outline" disabled={pdfBusy} onClick={() => void onQrEinePalette()}>
            {pdfBusy ? 'Bitte warten…' : 'QR-Etiketten drucken (1 Palette)'}
          </Button>
          <span className="text-xs text-muted">
            2 kleine QR-Zettel für Palette {ersteOffenePalette.paletteNo ?? `#${ersteOffenePalette.id}`}
          </span>
        </div>
      )}


    </div>
  )
}

export default function ReceiptDetailPage() {
  const { id } = useParams()
  const receiptId = Number(id)
  const { warehouse } = useAuth()
  const detail = useReceiptDetail(Number.isFinite(receiptId) ? receiptId : null)
  const actions = useReceiptAction(receiptId)

  const [under, setUnder] = useState({ reason: 'weniger_geliefert', qty_missing: 0, note: '' })
  // F5-Fix (2026-07-17): Legacy-Nachdruck-Pfad NUR fuer reine V1-Auftraege (unten
  // gated `isV1Receipt`). Beim WE-V2-Umbau (150c3c0) ging der Button verloren —
  // V1-Aufträge (Alt-Bestand) haben aber weiterhin die alte /labels/regenerate/-UX
  // (Neudruck mit Anzahl x Menge nach Storno). EIN stabiler Key je Versuch (RA-BI).
  const [regen, setRegen] = useState({ count: 0, qty: 0 })
  const regenKeyRef = useRef(newIdempotencyKey())
  // RA-WI4: actions.duplicate.isPending endet, sobald die Mutation selbst aufgeloest ist —
  // der anschliessende PDF-Download laeuft aber noch. Ein lokaler Busy-State ueber
  // Mutation+Download hinweg verhindert, dass ein Re-Klick waehrend des Downloads eine
  // ZWEITE Duplikat-Mutation ausloest.
  const [duplicateBusy, setDuplicateBusy] = useState(false)
  // RA-W-Minor: die Kopf-PDF-Buttons hatten weder Pending-Guard noch Fehler-Toast —
  // gleiches Muster wie InventurDetailPage.tsx onDownload(). Task 11: drei
  // moegliche Kopf-PDFs (Front-A4/Seiten-Etiketten/V1-Legacy) teilen sich EINEN
  // Busy-Flag (Enum statt boolean), da nur ein Download gleichzeitig sinnvoll ist.
  // 2026-10-15: dazu Front/Seite nur fuer die nachgemeldeten Etiketten („hinzugefügt").
  const [headerPdfBusy, setHeaderPdfBusy] = useState<'front' | 'side' | 'front-added' | 'side-added' | 'v1' | 'packstation' | null>(null)
  const vorschlag = useWePackstationVorschlag(receiptId)
  const [correctTarget, setCorrectTarget] = useState<{
    label: ReceiptLabel
    booked: ReceiptDetail['bookedInfo'][number]
  } | null>(null)
  const [cancelLabel, setCancelLabel] = useState<ReceiptLabel | null>(null)
  const [cancelReason, setCancelReason] = useState('')
  const [underOpen, setUnderOpen] = useState(false)
  const [regenOpen, setRegenOpen] = useState(false)
  const [cancelItemTarget, setCancelItemTarget] = useState<ReceiptDetail['items'][number] | null>(null)

  if (detail.isLoading) return <div className="p-4">Lade…</div>
  if (!detail.data && detail.isError && !(detail.error instanceof ApiError && detail.error.status === 404)) return <div className="p-4" role="alert">Auftrag konnte nicht geladen werden. <Button variant="outline" onClick={() => void detail.refetch()}>Erneut laden</Button></div>
  if (!detail.data) return <div className="p-4">Auftrag nicht gefunden. <Link className="text-primary underline" to="/warehouse/wareneingang">Zurück</Link></div>
  const d = detail.data
  const item = d.items[0]
  // Task 11 (Welle-A-Vertrag): die alte /labels-pdf/-Route antwortet bei V2-Auftraegen
  // mit 400 — der V1-Button darf NUR erscheinen, wenn KEINE Position ein
  // cartonsDelivered (V2-Soll-Kartons) traegt.
  const isV1Receipt = d.items.every((i) => i.cartonsDelivered == null)
  const hasPalletItems = d.items.some(i => !(i.divertedCartons > 0 && i.totalQty === 0 && i.cartonsDelivered === 0))
  const hatPackstation = d.items.some(i => i.divertedCartons > 0 && i.status !== 'storniert')
  // Gleiche Auswahl wie buildWeLabelData(scope 'added') im Backend: nachgemeldet, nicht storniert, mit Position.
  const addedCount = d.labels.filter((l) => l.isAdded === true && l.status !== 'cancelled' && l.receiptItemId != null).length
  async function act<T>(p: Promise<T>, ok: string) {
    try { await p; toast.success(ok) } catch (e) { toast.error(e instanceof Error ? e.message : 'Fehlgeschlagen') }
  }

  // RA-W-Minor: Pending-Guard + try/catch + Fehler-Toast, gleiches Muster wie
  // InventurDetailPage.tsx onDownload().
  async function onVorschlag() {
    if (vorschlag.isPending) return
    try {
      const res = await vorschlag.mutateAsync()
      const gesetzt = res.gesetzt.map(g => `${g.merchant_sku}: ${g.kartons}`).join(', ')
      if (res.gesetzt.length > 0) toast.success(`Packstation-Kartons gesetzt — ${gesetzt}`)
      else toast.info('Kein leeres Packstation-Fach in diesem Auftrag')
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Leere Fächer konnten nicht aufgefüllt werden')
    }
  }

  async function onHeaderPdf(kind: 'front' | 'side' | 'front-added' | 'side-added' | 'v1' | 'packstation') {
    if (headerPdfBusy) return
    setHeaderPdfBusy(kind)
    try {
      if (kind === 'front') await downloadPdf(`/warehouse/receipt/${receiptId}/we-front-pdf/`, `we-front-auftrag-${receiptId}.pdf`)
      else if (kind === 'side') await downloadPdf(`/warehouse/receipt/${receiptId}/we-side-pdf/`, `we-seiten-auftrag-${receiptId}.pdf`)
      else if (kind === 'front-added') await downloadPdf(`/warehouse/receipt/${receiptId}/we-front-pdf/`, `we-front-auftrag-${receiptId}-hinzugefuegt.pdf`, { scope: 'added' })
      else if (kind === 'side-added') await downloadPdf(`/warehouse/receipt/${receiptId}/we-side-pdf/`, `we-seiten-auftrag-${receiptId}-hinzugefuegt.pdf`, { scope: 'added' })
      else if (kind === 'packstation') await downloadPdf(`/warehouse/receipt/${receiptId}/packstation-liste-pdf/`, `packstation-liste-we-${receiptId}.pdf`)
      else await downloadPdf(`/warehouse/receipt/${receiptId}/labels-pdf/`, `paletten-zettel-${receiptId}.pdf`)
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Download fehlgeschlagen')
    } finally {
      setHeaderPdfBusy(null)
    }
  }

  // RA-WI4: Busy-Flag haelt ueber Mutation UND den nachfolgenden PDF-Download an — ein
  // Retry waehrend des Downloads darf NICHT nochmal die Duplikat-Mutation ausloesen.
  // Review Task 11: die alte /labels-pdf/-Route antwortet bei V2-Auftraegen mit 400
  // (Duplikat-Label entsteht in der DB, aber kein druckbares PDF). Bei V2 stattdessen
  // NACHEINANDER we-front-pdf/ + we-side-pdf/ laden — gleiches Muster wie
  // PositionSection.onReportPalettes (Zeilen ~89-90).
  async function onDuplicate(lid: number) {
    if (duplicateBusy) return
    setDuplicateBusy(true)
    try {
      await actions.duplicate.mutateAsync(lid)
      toast.success('Duplikat erzeugt — PDF lädt')
      if (isV1Receipt) {
        await downloadPdf(`/warehouse/receipt/${receiptId}/labels-pdf/`, `duplikat-${lid}.pdf`, { label_ids: [lid] })
      } else {
        await downloadPdf(`/warehouse/receipt/${receiptId}/we-front-pdf/`, `duplikat-${lid}-front.pdf`, { label_ids: [lid] })
        await downloadPdf(`/warehouse/receipt/${receiptId}/we-side-pdf/`, `duplikat-${lid}-seiten.pdf`, { label_ids: [lid] })
      }
    } catch (e) {
      toast.error(e instanceof Error ? e.message : 'Fehlgeschlagen')
    } finally {
      setDuplicateBusy(false)
    }
  }

  return (
    <div className="p-4 space-y-4 max-w-4xl mx-auto">
      {detail.isError && <div role="alert" className="flex flex-wrap items-center gap-2 text-sm">Aktualisierung fehlgeschlagen <Button size="sm" variant="outline" onClick={() => void detail.refetch()}>Erneut laden</Button></div>}
      <Link className="text-primary underline text-sm" to="/warehouse/wareneingang">← Liste</Link>
      <div className="rounded-xl border border-line bg-panel p-4 space-y-3">
        <div className="flex flex-wrap justify-between gap-2"><h1 className="text-xl font-bold">WE #{receiptId} · {d.items.length} {d.items.length === 1 ? 'Position' : 'Positionen'}{d.items.length === 1 && item?.merchantSku && <> · <GrundartikelLink code={item.merchantSku} /></>}</h1><StatusPill status={d.escalation.level === 'eskaliert' && d.receipt.status !== 'abgeschlossen' ? 'eskaliert' : d.progress.status} /></div>
        <div className="text-sm">{d.herkunft?.quelle === 'einkauf' ? 'Einkauf' : 'Leiter'} · {d.herkunft?.bestellung?.lieferant ?? 'Ohne Bestellung'} · {d.herkunft?.bestellung?.orderNumber ?? d.receipt.orderNo ?? '–'}{d.herkunft?.container && ` · ${d.herkunft.container.label ?? d.herkunft.container.containerNo ?? ''}`}</div>
        <div className="text-sm">Wareneingang: {d.receipt.firstInDate.split('-').reverse().join('.')}</div>
        {d.escalation.level === 'eskaliert' && d.receipt.status !== 'abgeschlossen' && <div className="text-sm text-red-800">Pflichtprüfung ({d.escalation.daysOpen} Tage)</div>}
        <div className="text-sm">{d.progress.booked} / {d.progress.expected} Paletten</div>
        <div role="progressbar" aria-label="Palettenfortschritt" aria-valuemin={0} aria-valuemax={d.progress.expected || 1} aria-valuenow={d.progress.booked} className="h-1.5 rounded bg-gray-200"><div className="h-1.5 rounded bg-primary" style={{width: `${d.progress.expected ? Math.min(100, d.progress.booked / d.progress.expected * 100) : 0}%`}} /></div>
        <div className="flex flex-wrap gap-2 items-center">
          <DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline">Drucken</Button></DropdownMenuTrigger><DropdownMenuContent align="start">
            {hasPalletItems && <><DropdownMenuItem disabled={headerPdfBusy !== null} onSelect={() => void onHeaderPdf('front')}>Front-A4 (alle)</DropdownMenuItem><DropdownMenuItem disabled={headerPdfBusy !== null} onSelect={() => void onHeaderPdf('side')}>Seiten-Etiketten (alle)</DropdownMenuItem>{addedCount > 0 && <><DropdownMenuItem disabled={headerPdfBusy !== null} onSelect={() => void onHeaderPdf('front-added')}>Front-A4 (hinzugefügt)</DropdownMenuItem><DropdownMenuItem disabled={headerPdfBusy !== null} onSelect={() => void onHeaderPdf('side-added')}>Seiten-Etiketten (hinzugefügt)</DropdownMenuItem></>}</>}
            {isV1Receipt && <DropdownMenuItem disabled={headerPdfBusy !== null} onSelect={() => void onHeaderPdf('v1')}>Etiketten-PDF (V1)</DropdownMenuItem>}
            {warehouse.lead && item && isV1Receipt && <DropdownMenuItem onSelect={() => setRegenOpen(true)}>Neue Etiketten nachdrucken</DropdownMenuItem>}
          </DropdownMenuContent></DropdownMenu>
          {(hatPackstation || (warehouse.lead && d.receipt.status !== 'abgeschlossen' && !isV1Receipt)) && <DropdownMenu><DropdownMenuTrigger asChild><Button variant="outline">Packstation</Button></DropdownMenuTrigger><DropdownMenuContent align="start">{hatPackstation && <DropdownMenuItem disabled={headerPdfBusy !== null} onSelect={() => void onHeaderPdf('packstation')}>Packstation-Liste (PDF)</DropdownMenuItem>}{warehouse.lead && d.receipt.status !== 'abgeschlossen' && !isV1Receipt && <DropdownMenuItem disabled={vorschlag.isPending} onSelect={() => void onVorschlag()}>Leere Packstation-Fächer auffüllen</DropdownMenuItem>}</DropdownMenuContent></DropdownMenu>}
          {warehouse.lead && item && <Button variant="outline" onClick={() => setUnderOpen(true)}>Unterlieferung zum Auftrag</Button>}
          {warehouse.lead && d.receipt.status !== 'abgeschlossen' && <Button className="ml-auto" disabled={actions.close.isPending} onClick={() => void act(actions.close.mutateAsync(undefined), 'Auftrag abgeschlossen')}>Auftrag abschließen</Button>}
        </div>{actions.close.error && <div role="alert">{actions.close.error.message}</div>}
      </div>

      {/* Task 11: je Position ein eigener Abschnitt mit eigener Etiketten-Tabelle
          statt einer gemeinsamen Tabelle ueber alle Positionen. C-I4 bleibt: alle
          Zeilen-Aktionen (Stornieren/Duplikat) waehrend EINER laufenden Mutation
          gesperrt — Positionen teilen sich denselben Auftrag, ein Doppel-Tap auf
          zwei Zeilen waehrend des ersten Requests soll nicht zwei Race-Buchungen
          ausloesen. */}
      {d.items.map((it) => (
        <PositionSection
          key={it.id}
          item={it}
          receiptId={receiptId}
          labels={d.labels.filter((l) => l.receiptItemId === it.id)}
          bookedInfo={d.bookedInfo}
          canLead={warehouse.lead}
          closed={d.receipt.status === 'abgeschlossen'}
          rowBusy={actions.cancelLabels.isPending || actions.duplicate.isPending || actions.cancelItem.isPending || duplicateBusy}
          onCancel={(lid) => { setCancelReason(''); setCancelLabel(d.labels.find(l => l.id === lid) ?? null) }}
          onDuplicate={(lid) => void onDuplicate(lid)}
          onCorrectPalette={(l, booked) => setCorrectTarget({ label: l, booked })}
          onCancelItem={(posItem) => setCancelItemTarget(posItem)}
        />
      ))}

      {/* F5-Fix: Legacy-Nachdruck NUR fuer reine V1-Auftraege — /labels/regenerate/
          erzeugt Etiketten MIT Vorab-Menge (V1); V2-Positionen nutzen dafuer den
          Ziel-Palettenzahl-Block je Position oben. */}
      <Dialog open={regenOpen} onOpenChange={setRegenOpen}><DialogContent><DialogHeader><DialogTitle>Neue Etiketten nachdrucken</DialogTitle><DialogDescription>Nach Storno · Zettelnummer läuft weiter</DialogDescription></DialogHeader><div className="flex gap-2 items-center flex-wrap">
            <Input className="w-20" type="number" placeholder="Anzahl" value={regen.count || ''} onChange={(e) => setRegen({ ...regen, count: +e.target.value })} />
            <span className="text-sm">Palette(n) à</span>
            <Input className="w-24" type="number" placeholder="Menge" value={regen.qty || ''} onChange={(e) => setRegen({ ...regen, qty: +e.target.value })} />
            <Button variant="outline" disabled={actions.regenerate.isPending || regen.count < 1 || regen.qty < 1}
              onClick={() => void act(
                actions.regenerate.mutateAsync({
                  receipt_item_id: item!.id, groups: [{ count: regen.count, qty: regen.qty }],
                  idempotency_key: regenKeyRef.current,
                }).then((res) => { regenKeyRef.current = newIdempotencyKey(); return res }),
                'Neue Etiketten erzeugt',
              )}>
              {actions.regenerate.isPending ? 'Drucke…' : 'Nachdrucken'}
            </Button>
          </div></DialogContent></Dialog>
      <Dialog open={underOpen} onOpenChange={setUnderOpen}><DialogContent><DialogHeader><DialogTitle>Unterlieferung zum Auftrag</DialogTitle><DialogDescription>Fehlende Ware dokumentieren.</DialogDescription></DialogHeader><div className="flex flex-wrap gap-2 items-center">
            <select className="border border-line rounded-md px-2 py-1 text-sm bg-panel" value={under.reason} onChange={(e) => setUnder({ ...under, reason: e.target.value })}>
              {REASONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <Input className="w-28" type="number" placeholder="fehlende St." value={under.qty_missing || ''} onChange={(e) => setUnder({ ...under, qty_missing: +e.target.value })} />
            <Input className="w-64" placeholder="Notiz" value={under.note} onChange={(e) => setUnder({ ...under, note: e.target.value })} />
            <Button variant="outline" disabled={actions.underdelivery.isPending}
              onClick={() => void actions.underdelivery.mutateAsync({ reason: under.reason, qty_missing: under.qty_missing || undefined, note: under.note || undefined }).then(() => { toast.success('Unterlieferung dokumentiert'); setUnderOpen(false) }).catch(e => toast.error(e instanceof Error ? e.message : 'Fehlgeschlagen'))}>
              {actions.underdelivery.isPending ? 'Erfasse…' : 'Erfassen'}
            </Button>
          </div></DialogContent></Dialog>
      <details className="rounded-xl border border-line bg-panel p-4"><summary className="cursor-pointer font-semibold">Verlauf ({d.log.length})</summary><ul className="text-sm space-y-1">
          {d.log.map((m) => (
            <li key={m.id} className="flex gap-2"><span className="text-muted whitespace-nowrap">{new Date(m.createdAt).toLocaleString('de-DE')}</span><span>{({ place: 'Eingelagert', place_misch: 'Mischpalette eingelagert', we_erfassung: 'Wareneingang erfasst', correct: 'Korrigiert' } as Record<string, string>)[m.action] ?? m.action.replaceAll('_', ' ')}</span><span>{m.note} — {m.actorName ?? '?'}</span></li>
          ))}
        </ul>{d.log.length === 0 && <p className="text-sm text-muted">Kein Verlauf.</p>}</details>
      <Dialog open={cancelLabel !== null} onOpenChange={open => { if (!open) setCancelLabel(null) }}><DialogContent><DialogHeader><DialogTitle>Etikett stornieren</DialogTitle><DialogDescription>{cancelLabel?.paletteNo ?? 'Palette'} · Zettel {cancelLabel?.sheetNo} · {d.items.find(i => i.id === cancelLabel?.receiptItemId)?.merchantSku}</DialogDescription></DialogHeader><Label htmlFor="label-cancel-reason">Storno-Grund</Label><Input id="label-cancel-reason" value={cancelReason} onChange={e => setCancelReason(e.target.value)} /><DialogFooter><Button variant="outline" onClick={() => setCancelLabel(null)}>Abbrechen</Button><Button disabled={!cancelReason || actions.cancelLabels.isPending} onClick={() => { if (cancelLabel && cancelReason) void actions.cancelLabels.mutateAsync({label_ids: [cancelLabel.id], reason: cancelReason}).then(() => { toast.success('Etikett storniert'); setCancelLabel(null) }).catch(e => toast.error(e instanceof Error ? e.message : 'Fehlgeschlagen')) }}>Etikett stornieren</Button></DialogFooter></DialogContent></Dialog>

      <PaletteCorrectDialog
        open={correctTarget !== null}
        onOpenChange={(open) => { if (!open) setCorrectTarget(null) }}
        label={correctTarget?.label ?? null}
        booked={correctTarget?.booked}
      />

      <ItemCancelDialog
        open={cancelItemTarget !== null}
        onOpenChange={(open) => { if (!open) setCancelItemTarget(null) }}
        item={cancelItemTarget}
        receiptId={receiptId}
        openLabels={d.labels.filter(l => l.receiptItemId === cancelItemTarget?.id && l.status === 'valid').length}
      />
    </div>
  )
}
